import type { InsightDto } from "@home/shared";
import { useState } from "react";
import { ApiError, api } from "../api.ts";
import { money, shortDate } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { useRemote } from "../useRemote.ts";

const KIND_ICON: Record<InsightDto["tips"][number]["kind"], string> = {
  save: "💡",
  earn: "📈",
  savings: "🐷",
  warning: "⚠️",
  praise: "👍",
  debt: "🏦",
};

const HEALTH_ICON: Record<InsightDto["health"], string> = { good: "🟢", ok: "🟡", risk: "🔴" };

function errorText(err: unknown): string {
  if (err instanceof ApiError && (err.code === "err.insight_limit" || err.code === "err.insight_no_data")) return t(err.code);
  return t("loadError");
}

export function InsightCard({ lang, online }: { lang: Lang; online: boolean }) {
  const remote = useRemote("insight", () => api.insight().then((r) => r.insight));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<InsightDto | null>(null);
  const insight = fresh ?? remote.data;

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      setFresh((await api.refreshInsight()).insight);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dash-card insight">
      <div className="dash-card-head">
        <h3>✨ {t("aiTitle")}</h3>
        {insight && (
          <span className={`health health-${insight.health}`}>
            {HEALTH_ICON[insight.health]} {t(`health.${insight.health}`)}
          </span>
        )}
      </div>

      {busy && <p className="hint">⏳ {t("aiLoading")}</p>}

      {!busy && !insight && (
        <>
          <p className="insight-empty">{t("aiEmpty")}</p>
          <button className="btn btn-primary wide" disabled={!online} onClick={() => void generate()}>
            {t("aiGet")}
          </button>
        </>
      )}

      {!busy && insight && (
        <>
          <p className="insight-summary">{insight.summary}</p>
          <ul className="tips">
            {insight.tips.map((tip, i) => (
              <li key={i} className={`tip tip-${tip.kind}`}>
                <span className="tip-icon" aria-hidden="true">
                  {KIND_ICON[tip.kind]}
                </span>
                <div>
                  <div className="tip-title">{tip.title}</div>
                  <div className="tip-text">{tip.text}</div>
                  {tip.monthlyImpact && (
                    <div className="tip-impact">{t("impact", { amount: money(tip.monthlyImpact, "UZS", lang) })}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="insight-foot">
            <span className="hint">{t("aiAt", { date: shortDate(insight.createdAt) })}</span>
            {insight.canRefresh ? (
              <button className="btn btn-small-ghost" disabled={!online} onClick={() => void generate()}>
                ↻ {t("aiRefresh")}
              </button>
            ) : (
              <span className="hint">{insight.nextRefreshAt ? t("aiNext", { date: shortDate(`${insight.nextRefreshAt}T00:00:00+05:00`) }) : t("aiLimit")}</span>
            )}
          </div>
        </>
      )}

      {error && <p className="insight-error">{error}</p>}
    </section>
  );
}
